import React, {forwardRef, useImperativeHandle, useMemo, useRef, useState} from 'react'
import IconCamera from '../../assets/icons/cameraIcon.svg?react';
import { IconComponent } from '../../types'
import { Button } from '../Button'
import { Dialog } from '../Dialog'

import {useTranslation} from "react-i18next";
import {getBreakpoint} from "../../utils/getBreakpoint.ts";
import {Input} from "../Input.tsx";
import {Select} from "../Select.tsx";
import {useSelector} from "react-redux";
import {RootState} from "../../store.ts";
import {AutocompleteSearch} from "../AutocompleteSearch.tsx";
import {useAgencies} from "../../hooks/useAgencies.ts";
import {InvitationLandingTemplate, ReviewerType} from "../../domain/entities/Agency.ts";
import {getMarketOptions} from "../agenciesTable/marketOptions.ts";
import IconSpinner from '../../assets/icons/spinner.svg?react';
import {AgencyCreatedDialog} from './AgencyCreatedDialog.tsx';

type ConfirmDialogProps = {
  title?: string
  message?: string
  confirmLabel?: string
  confirmIcon?: IconComponent
  cancelLabel?: string
  children?: React.ReactNode
    onComplete: any
}

const currentBreakpoint = getBreakpoint()


export const CreateUpdateAgencyDialog = forwardRef<
  React.ElementRef<typeof Dialog<boolean>>,
  ConfirmDialogProps
>(({ title, confirmLabel,onComplete }, ref) => {

    const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)
    const fileInput = useRef<HTMLInputElement>(null)
    const colorInputRef = useRef<HTMLInputElement>(null)
    const createdDialog = useRef<React.ElementRef<typeof AgencyCreatedDialog>>(null)

  const { t } = useTranslation()

    const { createAgency, isCreatingAgency, agencies } = useAgencies()

    const user = useSelector((state: RootState) => state.auth.user)
    const isAdmin = user?.role === 'admin'

    // Contact person
    const [contactFirstName, setContactFirstName] = useState<string>('')
    const [contactLastName, setContactLastName] = useState<string>('')
    const [contactPhone, setContactPhone] = useState<string>('')
    const [contactEmail, setContactEmail] = useState<string>('')

    // Agency data
    const [agencyName, setAgencyName] = useState<string>('')
    const [agencyWebsite, setAgencyWebsite] = useState<string>('')
    const [market, setMarket] = useState<string>('ES')

    // Brand
    const [agencyColor, setAgencyColor] = useState<string>('')
    const [coverMainColorHex, setCoverMainColorHex] = useState<string>('#FFFFFF')
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [invitationLandingTemplate, setInvitationLandingTemplate] = useState<InvitationLandingTemplate | ''>('beach')

    // Code prefix
    const [codePrefix, setCodePrefix] = useState<string>('')

    // Reviewer
    const [reviewer, setReviewer] = useState<ReviewerType>('Traveler')

    // Redemption code
    const [genericCode, setGenericCode] = useState<string>('')
    const [codeFormat, setCodeFormat] = useState<'generic' | 'unique'>('unique')

    // Referral
    const [referralSearch, setReferralSearch] = useState<string>('')
    const [referredByAgencyId, setReferredByAgencyId] = useState<string | undefined>(undefined)
    const [referralAgencyName, setReferralAgencyName] = useState<string>('')
    const [referralCommissionPct, setReferralCommissionPct] = useState<string>('10')
    const [referralValidFrom, setReferralValidFrom] = useState<string>(() => new Date().toISOString().split('T')[0])
    const [referralValidUntil, setReferralValidUntil] = useState<string>('')

    // 51trips-only extra fields
    const [numberOfPagesIncluded, setNumberOfPagesIncluded] = useState<number>(60)
    const [clientPlannerUrl, setClientPlannerUrl] = useState<string>('')
    const [tripbookAgencyBindingName, setTripbookAgencyBindingName] = useState<string>('')
    const [invertedLogo, setInvertedLogo] = useState<boolean>(false)
    const [webLinkQRCredits, setWebLinkQRCredits] = useState<boolean>(false)
    const [dedicationText, setDedicationText] = useState<string>('')
    const [fiscalName, setFiscalName] = useState<string>('')
    const [fiscalAddress, setFiscalAddress] = useState<string>('')
    const [fiscalId, setFiscalId] = useState<string>('')
    const [enableFirstPackage, setEnableFirstPackage] = useState<boolean>(false)
    const [firstPackageUnitPrice, setFirstPackageUnitPrice] = useState<string>('')
    const [firstPackageQuantity, setFirstPackageQuantity] = useState<string>('1')
    const [firstPackageDate, setFirstPackageDate] = useState<string>('')
    const [showLogoInIntro, setShowLogoInIntro] = useState<boolean>(false)
    const [introductionText, setIntroductionText] = useState<string>('')

    // Invitation engagement flow
    const DEFAULT_ENGAGEMENT_FLOW = 'itinerary,experienceSelection,login,contactPhone'
    const [captureItinerary, setCaptureItinerary] = useState<boolean>(true)
    const [captureDIY, setCaptureDIY] = useState<boolean>(true)

    const buildEngagementFlow = (itinerary: boolean, diy: boolean): string => {
        const base = DEFAULT_ENGAGEMENT_FLOW.split(',')
        return base.filter(step => {
            if (step === 'itinerary') return itinerary
            if (step === 'experienceSelection') return diy
            return true
        }).join(',')
    }

    const isFormValid = agencyName.trim()
        && (!isAdmin || (contactFirstName.trim() && contactLastName.trim()))
        && (codeFormat === 'unique' || genericCode.trim())

    const agencyOptions = useMemo(() => {
        if (!agencies || !referralSearch) return []
        return agencies
            .filter((a: any) => a.nameLowerCase?.includes(referralSearch.toLowerCase()))
            .map((a: any) => ({ value: a.id, label: a.name, data: a }))
            .slice(0, 10)
    }, [agencies, referralSearch])

  /// Effects


  /// Memos



  useImperativeHandle(
      ref,
      () => ({
        open: () => {
          return dialog.current!.open()
        },
        close: dialog.current!.close
      }),
      []
  )


    const handleFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || [])
        setLogoFile(files[0])
        event.target.value = ''
    }

    const handleCreate = () => {
        createAgency({
            name: agencyName,
            contactFirstName,
            contactLastName,
            contactPhone: contactPhone || undefined,
            contactEmail: contactEmail || undefined,
            website: agencyWebsite || undefined,
            market: market || undefined,
            logo: logoFile ?? undefined,
            primaryColor: agencyColor || undefined,
            coverMainColorHex: coverMainColorHex || '#FFFFFF',
            genericCode: codeFormat === 'generic' ? genericCode : undefined,
            codeFormat,
            codePrefix: codePrefix || undefined,
            reviewer,
            referredByAgencyId,
            referralCommissionPct: referredByAgencyId ? (parseFloat(referralCommissionPct) / 100) : undefined,
            referralValidFrom: referredByAgencyId ? new Date(referralValidFrom).getTime() : undefined,
            referralValidUntil: referredByAgencyId && referralValidUntil ? new Date(referralValidUntil).getTime() : undefined,
            invitationLandingTemplate: invitationLandingTemplate || undefined,
            numberOfPagesIncluded: isAdmin ? numberOfPagesIncluded : undefined,
            clientPlannerUrl: isAdmin ? (clientPlannerUrl || undefined) : undefined,
            tripbookAgencyBindingName: isAdmin ? (tripbookAgencyBindingName || undefined) : undefined,
            invertedLogo: isAdmin ? invertedLogo : undefined,
            webLinkQRCredits: isAdmin ? webLinkQRCredits : undefined,
            dedicationText: isAdmin ? (dedicationText || undefined) : undefined,
            fiscalName: isAdmin ? (fiscalName || undefined) : undefined,
            fiscalAddress: isAdmin ? (fiscalAddress || undefined) : undefined,
            fiscalId: isAdmin ? (fiscalId || undefined) : undefined,
            firstPackage: (isAdmin && enableFirstPackage && firstPackageUnitPrice && firstPackageDate)
                ? {
                    unitPrice: parseFloat(firstPackageUnitPrice),
                    quantity: parseInt(firstPackageQuantity) || 1,
                    date: new Date(firstPackageDate).getTime(),
                }
                : undefined,
            showLogoInIntro,
            introductionText: introductionText || undefined,
            invitationEngagementFlow: buildEngagementFlow(captureItinerary, captureDIY),
        }).then(() => {
            onComplete()
            dialog.current!.close(true)
            createdDialog.current?.open(agencyName.trim())
        })
    }

  return (
    <>
    <Dialog ref={dialog} title={title}
            customWidth={currentBreakpoint.isMobile ? undefined : '80vw'}
            width={currentBreakpoint.isMobile ? 'full' : undefined}
            height={currentBreakpoint.isMobile ? 'full' : 'auto'}
            withHeaderDelimiter
            headerButtons={[
        <Button
            key="close"
            variant="primary"
            size="small"
            disabled={isCreatingAgency || !isFormValid}
            onClick={handleCreate}
            className="max-h-[32px]"
        >
            {t('create', { ns: 'dashboard' })}
        </Button>,
        <Button
            key="save"
            variant="secondary"
            size="small"
            onClick={() => {
                ref.current?.close()
            }}
            className="max-h-[32px]"
        >
            {t('cancel', { ns: 'dashboard' })}
        </Button>
    ]}>
        {
            isCreatingAgency ? <div className="absolute inset-0 bg-neutralBlack-100 opacity-75 flex rounded-xl">
                    <IconSpinner className="size-16 m-auto"/>
                </div> :

      <div id="createAgencyForm" className="flex flex-col gap-6">

          {/* ── Layout: 3 columnas en desktop (2 para no-51trips) ────────── */}
          <div className={`grid grid-cols-1 gap-6 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>

              {/* ── Columna 1: Persona de contacto (solo 51trips) ───────── */}
              {isAdmin && (
              <section className="flex flex-col gap-4">
                  <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                      {t('contactPerson', {ns: 'dashboard'})}
                  </h3>
                  <div className="flex flex-col gap-1">
                      <label htmlFor="contactFirstName" className="text-[14px] font-medium">
                          <span className="text-danger mr-0.5">*</span>{t('firstName', {ns: 'dashboard'})}
                      </label>
                      <Input type="text" id="contactFirstName" invalid={false} value={contactFirstName}
                             onChange={(e) => setContactFirstName(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                  <div className="flex flex-col gap-1">
                      <label htmlFor="contactLastName" className="text-[14px] font-medium">
                          <span className="text-danger mr-0.5">*</span>{t('lastName', {ns: 'dashboard'})}
                      </label>
                      <Input type="text" id="contactLastName" invalid={false} value={contactLastName}
                             onChange={(e) => setContactLastName(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                  <div className="flex flex-col gap-1">
                      <label htmlFor="contactPhone" className="text-[14px] font-medium">
                          {t('phone', {ns: 'dashboard'})}
                      </label>
                      <Input type="tel" id="contactPhone" invalid={false} value={contactPhone}
                             onChange={(e) => setContactPhone(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                  <div className="flex flex-col gap-1">
                      <label htmlFor="contactEmail" className="text-[14px] font-medium">
                          {t('email', {ns: 'dashboard'})}
                      </label>
                      <Input type="email" id="contactEmail" invalid={false} value={contactEmail}
                             onChange={(e) => setContactEmail(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
              </section>
              )}

              {/* ── Columna 2: Datos de la agencia ──────────────────────── */}
              <section className="flex flex-col gap-4">
                  <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                      {t('agencyData', {ns: 'dashboard'})}
                  </h3>
                  <div className="flex flex-col gap-1">
                      <label htmlFor="agencyName" className="text-[14px] font-medium">
                          <span className="text-danger mr-0.5">*</span>{t('writeAgencyName', {ns: 'dashboard'})}
                      </label>
                      <Input type="text" id="agencyName" name="agencyName" invalid={false} value={agencyName}
                             onChange={(e) => setAgencyName(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                   <div className="flex flex-col gap-1">
                       <label htmlFor="website" className="text-[14px] font-medium">
                           {t('addAgencyWebsite', {ns: 'dashboard'})}
                       </label>
                       <Input type="text" id="website" name="website" invalid={false} value={agencyWebsite}
                              onChange={(e) => setAgencyWebsite(e.target.value)}
                              className="w-full h-[40px]"/>
                   </div>
                   <div className="flex flex-col gap-1">
                       <label htmlFor="codePrefix" className="text-[14px] font-medium">
                           {t('codePrefix', {ns: 'dashboard'})}
                       </label>
                       <Input type="text" id="codePrefix" name="codePrefix" invalid={false} value={codePrefix}
                              onChange={(e) => setCodePrefix(e.target.value)}
                              className="w-full h-[40px]"/>
                   </div>
                   <div className="flex flex-col gap-1">
                       <label htmlFor="reviewer" className="text-[14px] font-medium">
                           {t('reviewer', {ns: 'dashboard'})}
                       </label>
                       <Select
                           id="reviewer"
                           block
                           value={reviewer}
                           onChange={(e) => setReviewer(e.target.value as ReviewerType)}
                           options={[
                               { value: 'Traveler', label: t('reviewerTraveler', {ns: 'dashboard'}) },
                               { value: 'Agency',   label: t('reviewerAgency',   {ns: 'dashboard'}) },
                           ]}
                       />
                   </div>
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="market" className="text-[14px] font-medium">
                          {t('primaryMarket', {ns: 'dashboard'})}
                      </label>
                      <Select
                          id="market"
                          block
                          value={market}
                          onChange={(e) => setMarket(e.target.value)}
                          placeholder={t('selectPrimaryMarket', {ns: 'dashboard'})}
                          options={getMarketOptions()}
                      />
                  </div>
                  )}
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="numberOfPagesIncluded" className="text-[14px] font-medium">
                          {t('numberOfPagesIncluded', {ns: 'dashboard'})}
                      </label>
                      <Input type="number" id="numberOfPagesIncluded" invalid={false}
                             value={numberOfPagesIncluded}
                             onChange={(e) => setNumberOfPagesIncluded(parseInt(e.target.value) || 60)}
                             className="w-full h-[40px]"/>
                  </div>
                  )}
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="clientPlannerUrl" className="text-[14px] font-medium">
                          {t('clientPlannerUrl', {ns: 'dashboard'})}
                      </label>
                      <Input type="url" id="clientPlannerUrl" invalid={false}
                             value={clientPlannerUrl}
                             onChange={(e) => setClientPlannerUrl(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                  )}
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="tripbookAgencyBindingName" className="text-[14px] font-medium">
                          {t('tripbookAgencyBindingName', {ns: 'dashboard'})}
                      </label>
                      <Input type="text" id="tripbookAgencyBindingName" invalid={false}
                             value={tripbookAgencyBindingName}
                             onChange={(e) => setTripbookAgencyBindingName(e.target.value)}
                             className="w-full h-[40px]"/>
                  </div>
                  )}
              </section>

              {/* ── Columna 3: Marca ─────────────────────────────────────── */}
              <section className="flex flex-col gap-4">
                  <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                      {t('brandInfo', {ns: 'dashboard'})}
                  </h3>

                  {/* Logo */}
                  <div className="flex flex-col gap-1">
                      <label htmlFor="logoFile" className="text-[14px] font-medium">
                          {t('addAgencyLogo', {ns: 'dashboard'})}
                      </label>
                      <input
                          type="file"
                          ref={fileInput}
                          className="hidden"
                          onChange={handleFiles}
                          accept={"image/png"}
                          multiple={false}
                      />
                      <div className={(logoFile ? "border border-neutralBlack-200" : "") + " w-[96px] h-[96px] bg-white rounded-full flex items-center cursor-pointer"}
                           onClick={() => fileInput.current?.click()}>
                          {logoFile
                              ? <img className="p-2 h-full object-contain" src={URL.createObjectURL(logoFile)} alt="logo"/>
                              : <IconCamera className="cursor-pointer w-[96px] h-[96px] size-full flex items-center"/>}
                          {logoFile ? <div className="relative top-7 right-6"><IconCamera/></div> : null}
                      </div>
                  </div>

                   {/* Color corporativo con picker */}
                   <div className="flex flex-col gap-1">
                       <label htmlFor="agencyColor" className="text-[14px] font-medium">
                           {t('agencyCorporativeColor', {ns: 'dashboard'})}
                       </label>
                       <div className="text-[12px] text-neutralBlack-500">{t('agencyCorporativeColorAux', {ns: 'dashboard'})}</div>
                       <div className="flex items-center gap-3">
                           <button
                               type="button"
                               className="w-[40px] h-[40px] rounded-md border border-neutralBlack-300 shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                               style={{ backgroundColor: agencyColor || '#ffffff' }}
                               onClick={() => colorInputRef.current?.click()}
                               aria-label={t('agencyCorporativeColor', {ns: 'dashboard'})}
                           />
                           <Input
                               type="text"
                               id="agencyColor"
                               name="agencyColor"
                               invalid={false}
                               placeholder="#000000"
                               value={agencyColor}
                               onChange={(e) => setAgencyColor(e.target.value)}
                               className="h-[40px] flex-1"
                           />
                           <input
                               ref={colorInputRef}
                               type="color"
                               value={agencyColor || '#000000'}
                               onChange={(e) => setAgencyColor(e.target.value)}
                               className="sr-only"
                           />
                       </div>
                   </div>

                   {/* Cover main color con picker */}
                   <div className="flex flex-col gap-1">
                       <label htmlFor="coverMainColorHex" className="text-[14px] font-medium">
                           {t('coverMainColor', {ns: 'dashboard'})}
                       </label>
                       <div className="flex items-center gap-3">
                           <button
                               type="button"
                               className="w-[40px] h-[40px] rounded-md border border-neutralBlack-300 shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                               style={{ backgroundColor: coverMainColorHex }}
                               onClick={() => {
                                   const inp = document.getElementById('coverMainColorPicker') as HTMLInputElement
                                   inp?.click()
                               }}
                               aria-label={t('coverMainColor', {ns: 'dashboard'})}
                           />
                           <Input
                               type="text"
                               id="coverMainColorHex"
                               invalid={false}
                               placeholder="#FFFFFF"
                               value={coverMainColorHex}
                               onChange={(e) => setCoverMainColorHex(e.target.value)}
                               className="h-[40px] flex-1"
                           />
                           <input
                               id="coverMainColorPicker"
                               type="color"
                               value={coverMainColorHex}
                               onChange={(e) => setCoverMainColorHex(e.target.value)}
                               className="sr-only"
                           />
                       </div>
                   </div>

                   {/* Invitation landing template (solo 51trips) */}
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="invitationLandingTemplate" className="text-[14px] font-medium">
                          {t('invitationLandingTemplate', {ns: 'dashboard'})}
                      </label>
                      <Select
                          id="invitationLandingTemplate"
                          block
                          value={invitationLandingTemplate}
                          onChange={(e) => setInvitationLandingTemplate(e.target.value as InvitationLandingTemplate | '')}
                          placeholder={t('selectInvitationLandingTemplate', {ns: 'dashboard'})}
                          options={[
                              { value: 'safari', label: t('templateSafari', {ns: 'dashboard'}) },
                              { value: 'snow', label: t('templateSnow', {ns: 'dashboard'}) },
                              { value: 'jungle', label: t('templateJungle', {ns: 'dashboard'}) },
                              { value: 'beach', label: t('templateBeach', {ns: 'dashboard'}) },
                              { value: 'italy', label: t('templateItaly', {ns: 'dashboard'}) },
                              { value: 'japon', label: t('templateJapon', {ns: 'dashboard'}) },
                              { value: 'rocky', label: t('templateRocky', {ns: 'dashboard'}) },
                              { value: 'cruise', label: t('templateCruise', {ns: 'dashboard'}) },
                              { value: 'newyork', label: t('templateNewYork', {ns: 'dashboard'}) },
                              { value: 'international', label: t('templateInternational', {ns: 'dashboard'}) },
                              { value: 'golf', label: t('templateGolf', {ns: 'dashboard'}) }

                          ]}
                      />
                  </div>
                  )}

                  {/* Inverted logo (solo 51trips) */}
                  {isAdmin && (
                  <div className="flex items-center gap-3">
                      <input
                          type="checkbox"
                          id="invertedLogo"
                          checked={invertedLogo}
                          onChange={(e) => setInvertedLogo(e.target.checked)}
                          className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                      />
                      <label htmlFor="invertedLogo" className="text-[14px] font-medium cursor-pointer">
                          {t('invertedLogo', {ns: 'dashboard'})}
                      </label>
                  </div>
                  )}

                  {/* Web link QR credits (solo 51trips) */}
                  {isAdmin && (
                  <div className="flex items-center gap-3">
                      <input
                          type="checkbox"
                          id="webLinkQRCredits"
                          checked={webLinkQRCredits}
                          onChange={(e) => setWebLinkQRCredits(e.target.checked)}
                          className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                      />
                      <label htmlFor="webLinkQRCredits" className="text-[14px] font-medium cursor-pointer">
                          {t('webLinkQRCredits', {ns: 'dashboard'})}
                      </label>
                  </div>
                  )}

                  {/* Dedication text (solo 51trips) */}
                  {isAdmin && (
                  <div className="flex flex-col gap-1">
                      <label htmlFor="dedicationText" className="text-[14px] font-medium">
                          {t('dedicationText', {ns: 'dashboard'})}
                      </label>
                      <textarea
                          id="dedicationText"
                          value={dedicationText}
                          onChange={(e) => setDedicationText(e.target.value)}
                          rows={3}
                          className="w-full rounded-md border border-neutralBlack-300 px-3 py-2 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                  </div>
                  )}

                  {/* Show logo in intro */}
                  <div className="flex items-center gap-3">
                      <input
                          type="checkbox"
                          id="showLogoInIntro"
                          checked={showLogoInIntro}
                          onChange={(e) => setShowLogoInIntro(e.target.checked)}
                          className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                      />
                      <label htmlFor="showLogoInIntro" className="text-[14px] font-medium cursor-pointer">
                          {t('showLogoInIntro', {ns: 'dashboard'})}
                      </label>
                  </div>

                   {/* Introduction text */}
                   <div className="flex flex-col gap-1">
                       <label htmlFor="introductionText" className="text-[14px] font-medium">
                           {t('introductionText', {ns: 'dashboard'})}
                       </label>
                       <textarea
                           id="introductionText"
                           value={introductionText}
                           onChange={(e) => setIntroductionText(e.target.value)}
                           rows={3}
                           className="w-full rounded-md border border-neutralBlack-300 px-3 py-2 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                       />
                   </div>

                   {/* Invitation engagement flow */}
                   <div className="flex flex-col gap-2">
                       <label className="text-[14px] font-medium">
                           {t('invitationEngagementFlow', {ns: 'dashboard'})}
                       </label>
                       <div className="flex items-center gap-3">
                           <input
                               type="checkbox"
                               id="captureItinerary"
                               checked={captureItinerary}
                               onChange={(e) => setCaptureItinerary(e.target.checked)}
                               className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                           />
                           <label htmlFor="captureItinerary" className="text-[14px] cursor-pointer">
                               {t('captureItinerary', {ns: 'dashboard'})}
                           </label>
                       </div>
                       <div className="flex items-center gap-3">
                           <input
                               type="checkbox"
                               id="captureDIY"
                               checked={captureDIY}
                               onChange={(e) => setCaptureDIY(e.target.checked)}
                               className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                           />
                           <label htmlFor="captureDIY" className="text-[14px] cursor-pointer">
                               {t('captureDIY', {ns: 'dashboard'})}
                           </label>
                       </div>
                   </div>
               </section>

          </div>

          {/* ── Secciones 51trips (redemption + referral) ────────────────── */}
          {isAdmin && (
              <>
                  <div className="border-t border-neutralBlack-200"/>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      {/* Código de redemption */}
                      <section className="flex flex-col gap-3">
                          <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                              {t('redemptionCode', {ns: 'dashboard'})}
                          </h3>
                          <div className="flex flex-col gap-3">
                              <div className="flex flex-col gap-1">
                                  <label className="text-[14px] font-medium">
                                      {t('codeFormat', {ns: 'dashboard'})}
                                  </label>
                                  <div className="flex gap-0 rounded-[6px] border border-neutralBlack-300 overflow-hidden w-fit">
                                      <button
                                          type="button"
                                          onClick={() => setCodeFormat('unique')}
                                          className={`px-4 py-2 text-[13px] font-medium transition-colors duration-200 ease-out ${
                                              codeFormat === 'unique'
                                                  ? 'bg-primary text-white'
                                                  : 'bg-white text-neutralBlack-700 hover:bg-neutralBlack-100'
                                          }`}
                                      >
                                          {t('codeFormatUnique', {ns: 'dashboard'})}
                                      </button>
                                      <button
                                          type="button"
                                          onClick={() => { setCodeFormat('generic'); setGenericCode('') }}
                                          className={`px-4 py-2 text-[13px] font-medium border-l border-neutralBlack-300 transition-colors duration-200 ease-out ${
                                              codeFormat === 'generic'
                                                  ? 'bg-primary text-white'
                                                  : 'bg-white text-neutralBlack-700 hover:bg-neutralBlack-100'
                                          }`}
                                      >
                                          {t('codeFormatGeneric', {ns: 'dashboard'})}
                                      </button>
                                  </div>
                                  <p className="text-[12px] text-neutralBlack-500 mt-0.5">
                                      {codeFormat === 'unique'
                                          ? t('codeFormatUniqueDesc', {ns: 'dashboard'})
                                          : t('codeFormatGenericDesc', {ns: 'dashboard'})
                                      }
                                  </p>
                              </div>
                              {codeFormat === 'generic' && (
                                  <div className="flex flex-col gap-1">
                                      <label htmlFor="genericCode" className="text-[14px] font-medium">
                                          {t('genericCode', {ns: 'dashboard'})}
                                      </label>
                                      <Input
                                          type="text"
                                          id="genericCode"
                                          name="genericCode"
                                          value={genericCode}
                                          onChange={(e) => setGenericCode(e.target.value)}
                                          invalid={false}
                                          className="w-full h-[40px]"
                                      />
                                  </div>
                              )}
                          </div>
                      </section>

                      {/* Referral */}
                      <section className="flex flex-col gap-3">
                          <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                              {t('referral', {ns: 'dashboard'})}
                          </h3>
                          {referredByAgencyId ? (
                              <div className="flex flex-col gap-3">
                                  <div className="flex items-center gap-2">
                                      <span className="text-[14px]">{referralAgencyName}</span>
                                      <button
                                          className="text-[12px] text-neutralBlack-500 underline"
                                          onClick={() => { setReferredByAgencyId(undefined); setReferralAgencyName(''); setReferralSearch('') }}
                                      >
                                          {t('remove', {ns: 'dashboard'})}
                                      </button>
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                      <div className="flex flex-col gap-1">
                                          <label className="text-[13px] font-medium">
                                              {t('referralCommissionPct', {ns: 'dashboard'})}
                                          </label>
                                          <Input
                                              type="number"
                                              min="0"
                                              max="100"
                                              step="0.1"
                                              value={referralCommissionPct}
                                              onChange={(e) => setReferralCommissionPct(e.target.value)}
                                              invalid={false}
                                              className="h-[36px]"
                                          />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                          <label className="text-[13px] font-medium">
                                              {t('referralValidFrom', {ns: 'dashboard'})}
                                          </label>
                                          <input
                                              type="date"
                                              value={referralValidFrom}
                                              onChange={(e) => setReferralValidFrom(e.target.value)}
                                              className="h-[36px] border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"
                                          />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                          <label className="text-[13px] font-medium">
                                              {t('referralValidUntil', {ns: 'dashboard'})}
                                          </label>
                                          <input
                                              type="date"
                                              value={referralValidUntil}
                                              onChange={(e) => setReferralValidUntil(e.target.value)}
                                              className="h-[36px] border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"
                                          />
                                      </div>
                                  </div>
                              </div>
                          ) : (
                              <div className="flex flex-col gap-1">
                                  <label className="text-[14px] font-medium">
                                      {t('referralAgency', {ns: 'dashboard'})}
                                  </label>
                                  <AutocompleteSearch
                                      options={agencyOptions}
                                      placeholder={t('searchYourAgency', {ns: 'dashboard'})}
                                      searchQuery={referralSearch}
                                      onSearchQueryChange={setReferralSearch}
                                      onSelectOption={(option) => {
                                          setReferredByAgencyId(option.value)
                                          setReferralAgencyName(option.label)
                                          setReferralSearch('')
                                      }}
                                  />
                              </div>
                          )}
                      </section>

                  </div>
              </>
          )}

          {/* ── Fiscal data + First package (51trips) ────────────────── */}
          {isAdmin && (
              <>
                  <div className="border-t border-neutralBlack-200"/>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                      {/* Datos fiscales */}
                      <section className="flex flex-col gap-3">
                          <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                              {t('fiscalData', {ns: 'dashboard'})}
                          </h3>
                          <div className="flex flex-col gap-4">
                              <div className="flex flex-col gap-1">
                                  <label htmlFor="fiscalName" className="text-[14px] font-medium">
                                      {t('fiscalName', {ns: 'dashboard'})}
                                  </label>
                                  <Input type="text" id="fiscalName" invalid={false}
                                         value={fiscalName}
                                         onChange={(e) => setFiscalName(e.target.value)}
                                         className="w-full h-[40px]"/>
                              </div>
                              <div className="flex flex-col gap-1">
                                  <label htmlFor="fiscalAddress" className="text-[14px] font-medium">
                                      {t('fiscalAddress', {ns: 'dashboard'})}
                                  </label>
                                  <Input type="text" id="fiscalAddress" invalid={false}
                                         value={fiscalAddress}
                                         onChange={(e) => setFiscalAddress(e.target.value)}
                                         className="w-full h-[40px]"/>
                              </div>
                              <div className="flex flex-col gap-1">
                                  <label htmlFor="fiscalId" className="text-[14px] font-medium">
                                      {t('fiscalId', {ns: 'dashboard'})}
                                  </label>
                                  <Input type="text" id="fiscalId" invalid={false}
                                         value={fiscalId}
                                         onChange={(e) => setFiscalId(e.target.value)}
                                         className="w-full h-[40px]"/>
                              </div>
                          </div>
                      </section>

                      {/* Primer paquete (opcional) */}
                      <section className="flex flex-col gap-3">
                          <h3 className="text-[13px] font-semibold text-neutralBlack-500 uppercase tracking-wide">
                              {t('firstPackage', {ns: 'dashboard'})}
                          </h3>
                          <div className="flex items-center gap-3">
                              <input
                                  type="checkbox"
                                  id="enableFirstPackage"
                                  checked={enableFirstPackage}
                                  onChange={(e) => setEnableFirstPackage(e.target.checked)}
                                  className="w-4 h-4 rounded border-neutralBlack-300 accent-primary cursor-pointer"
                              />
                              <label htmlFor="enableFirstPackage" className="text-[14px] font-medium cursor-pointer">
                                  {t('enableFirstPackage', {ns: 'dashboard'})}
                              </label>
                          </div>
                          {enableFirstPackage && (
                              <div className="flex flex-col gap-3">
                                  <div className="flex flex-col gap-1">
                                      <label htmlFor="firstPackageUnitPrice" className="text-[14px] font-medium">
                                          {t('unitPrice', {ns: 'dashboard'})}
                                      </label>
                                      <Input type="number" id="firstPackageUnitPrice" invalid={false}
                                             value={firstPackageUnitPrice}
                                             onChange={(e) => setFirstPackageUnitPrice(e.target.value)}
                                             className="w-full h-[40px]"/>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <label htmlFor="firstPackageQuantity" className="text-[14px] font-medium">
                                          {t('quantity', {ns: 'dashboard'})}
                                      </label>
                                      <Input type="number" id="firstPackageQuantity" invalid={false}
                                             value={firstPackageQuantity}
                                             onChange={(e) => setFirstPackageQuantity(e.target.value)}
                                             className="w-full h-[40px]"/>
                                  </div>
                                  <div className="flex flex-col gap-1">
                                      <label htmlFor="firstPackageDate" className="text-[14px] font-medium">
                                          {t('date', {ns: 'dashboard'})}
                                      </label>
                                      <Input type="date" id="firstPackageDate" invalid={false}
                                             value={firstPackageDate}
                                             onChange={(e) => setFirstPackageDate(e.target.value)}
                                             className="w-full h-[40px]"/>
                                  </div>
                              </div>
                          )}
                      </section>

                  </div>
              </>
          )}

      </div>
        }

    </Dialog>
    <AgencyCreatedDialog ref={createdDialog}/>
    </>
  )
})
